import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import {
  KeyRound,
  Plus,
  ShieldCheck,
  Ban,
  Copy,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Loader2,
  EyeOff,
  Globe,
  Laptop,
} from 'lucide-react';

// Feature 1 (2026-05) — multi-token management for the Windows printer agent.
const PrinterTokensPage = () => {
  const { api } = useAuth();
  const { t, formatDate } = useLanguage();

  const [tokens, setTokens] = useState([]);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create modal state
  const [createOpen, setCreateOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [isShared, setIsShared] = useState(false);
  const [creating, setCreating] = useState(false);

  // Newly created token reveal state (one-time view)
  const [newToken, setNewToken] = useState(null);
  const [copied, setCopied] = useState(false);

  // Revoke confirmation
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/printer-tokens', {
        params: { include_revoked: includeRevoked },
      });
      setTokens(res.data || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, includeRevoked]);

  useEffect(() => { fetchTokens(); }, [fetchTokens]);

  const handleCreate = async () => {
    if (!label.trim()) return;
    setCreating(true);
    try {
      const res = await api.post('/admin/printer-tokens', {
        label: label.trim(),
        is_shared: isShared,
      });
      setNewToken(res.data);
      setCreateOpen(false);
      setLabel('');
      setIsShared(false);
      fetchTokens();
    } catch (err) {
      alert(t('printerTokens.actionError', { detail: err.response?.data?.detail || err.message }));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tok) => {
    setRevoking(true);
    try {
      await api.post(`/admin/printer-tokens/${tok.id}/revoke`);
      setRevokeTarget(null);
      fetchTokens();
    } catch (err) {
      alert(t('printerTokens.actionError', { detail: err.response?.data?.detail || err.message }));
    } finally {
      setRevoking(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // noop
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    return formatDate(iso, {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl" data-testid="printer-tokens-page">
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <KeyRound size={22} /> {t('printerTokens.title')}
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              {t('printerTokens.desc')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            data-testid="printer-tokens-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors"
          >
            <Plus size={16} /> {t('printerTokens.newBtn')}
          </button>
        </header>

        {/* Filters */}
        <div className="flex items-center justify-between gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              data-testid="printer-tokens-include-revoked"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
              className="rounded accent-emerald-500"
            />
            {t('printerTokens.includeRevoked')}
          </label>
          <button
            type="button"
            onClick={fetchTokens}
            className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-slate-200"
            data-testid="printer-tokens-refresh"
          >
            <RefreshCw size={14} /> {t('printerTokens.refresh')}
          </button>
        </div>

        {error && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 p-3 text-sm flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Tokens table */}
        <div className="rounded-xl border border-slate-700 bg-slate-900/40 overflow-hidden">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-slate-400">
              <Loader2 className="animate-spin mr-2" size={18} /> {t('printerTokens.loading')}
            </div>
          ) : tokens.length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm" data-testid="printer-tokens-empty">
              {t('printerTokens.emptyPre')} <span className="text-emerald-400">{t('printerTokens.newBtn')}</span> {t('printerTokens.emptyPost')}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-800/60 text-slate-300 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 text-left">{t('printerTokens.colLabel')}</th>
                  <th className="px-4 py-3 text-left">{t('printerTokens.colType')}</th>
                  <th className="px-4 py-3 text-left">{t('printerTokens.colPreview')}</th>
                  <th className="px-4 py-3 text-left">{t('printerTokens.colCreated')}</th>
                  <th className="px-4 py-3 text-left">{t('printerTokens.colLastUsed')}</th>
                  <th className="px-4 py-3 text-left">{t('printerTokens.colStatus')}</th>
                  <th className="px-4 py-3 text-right">{t('printerTokens.colActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {tokens.map((tok) => (
                  <tr key={tok.id} data-testid={`printer-token-row-${tok.id}`} className={tok.revoked ? 'opacity-60' : ''}>
                    <td className="px-4 py-3 font-medium text-slate-100">{tok.label}</td>
                    <td className="px-4 py-3">
                      {tok.is_shared ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 text-xs">
                          <Globe size={12} /> {t('printerTokens.typeShared')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 text-xs">
                          <Laptop size={12} /> {t('printerTokens.typePersonal')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-300 text-xs">{tok.token_preview}</td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {fmtDate(tok.created_at)}
                      {tok.created_by_username && (
                        <div className="text-slate-500 mt-0.5">{t('printerTokens.createdBy', { user: tok.created_by_username })}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs">
                      {fmtDate(tok.last_used_at)}
                      {tok.last_used_ip && <div className="text-slate-500 mt-0.5">{tok.last_used_ip}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {tok.revoked ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 text-xs">
                          <Ban size={12} /> {t('printerTokens.statusRevoked')}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-xs">
                          <ShieldCheck size={12} /> {t('printerTokens.statusActive')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {!tok.revoked && (
                        <button
                          type="button"
                          onClick={() => setRevokeTarget(tok)}
                          data-testid={`printer-token-revoke-${tok.id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-500/40 hover:bg-red-500/10 text-red-300 text-xs transition-colors"
                        >
                          <Ban size={13} /> {t('printerTokens.revoke')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 text-xs text-slate-400 space-y-1">
          <p className="font-medium text-slate-300 flex items-center gap-1"><EyeOff size={14} /> {t('printerTokens.securityTitle')}</p>
          <p>{t('printerTokens.securityHashPre')} <strong>{t('printerTokens.securityHashEmph')}</strong> {t('printerTokens.securityHashPost')}</p>
          <p>{t('printerTokens.securityAudit')}</p>
        </div>
      </div>

      {/* ─────────── Create modal ─────────── */}
      {createOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="printer-tokens-create-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Plus size={18} /> {t('printerTokens.createTitle')}</h2>
            <div className="space-y-3">
              <label className="block text-sm">
                <span className="text-slate-300">{t('printerTokens.createLabelField')}</span>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  autoFocus
                  maxLength={80}
                  data-testid="printer-tokens-create-label"
                  className="mt-1 w-full px-3 py-2 rounded-md bg-slate-800 border border-slate-700 focus:outline-none focus:border-emerald-500 text-sm"
                  placeholder={t('printerTokens.createLabelPlaceholder')}
                />
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-slate-300">
                <input
                  type="checkbox"
                  data-testid="printer-tokens-create-shared"
                  checked={isShared}
                  onChange={(e) => setIsShared(e.target.checked)}
                  className="rounded accent-emerald-500"
                />
                {t('printerTokens.createSharedLabel')}
              </label>
              <p className="text-xs text-slate-500">
                {t('printerTokens.createSharedHint')}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setCreateOpen(false); setLabel(''); setIsShared(false); }}
                disabled={creating}
                className="px-4 py-2 rounded-md text-slate-300 hover:bg-slate-800 text-sm"
                data-testid="printer-tokens-create-cancel"
              >
                {t('printerTokens.cancel')}
              </button>
              <button
                type="button"
                onClick={handleCreate}
                disabled={creating || !label.trim()}
                data-testid="printer-tokens-create-submit"
                className="px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              >
                {creating && <Loader2 size={14} className="animate-spin" />}
                {t('printerTokens.create')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────── One-time token reveal modal ─────────── */}
      {newToken && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="printer-tokens-reveal-modal">
          <div className="bg-slate-900 border border-emerald-500/40 rounded-xl p-6 w-full max-w-2xl space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-emerald-300">
              <ShieldCheck size={18} /> {t('printerTokens.revealTitle')}
            </h2>
            <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 text-sm p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{newToken.token_warning}</span>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-slate-400">{t('printerTokens.revealLabelPrefix')} <span className="text-slate-200">{newToken.label}</span></p>
              <div className="relative">
                <input
                  type="text"
                  readOnly
                  value={newToken.token}
                  data-testid="printer-tokens-reveal-value"
                  className="w-full px-3 py-3 pr-24 rounded-md bg-slate-950 border border-slate-700 font-mono text-xs text-emerald-300 select-all"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(newToken.token)}
                  data-testid="printer-tokens-reveal-copy"
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs inline-flex items-center gap-1"
                >
                  {copied ? <><CheckCircle size={13} /> {t('printerTokens.copied')}</> : <><Copy size={13} /> {t('printerTokens.copy')}</>}
                </button>
              </div>
              <p className="text-xs text-slate-500">
                {t('printerTokens.revealConfigPre')}
                <code className="mx-1 px-1.5 py-0.5 rounded bg-slate-800 text-slate-300">X-Agent-Token</code>{t('printerTokens.revealConfigPost')}
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => { setNewToken(null); setCopied(false); }}
                data-testid="printer-tokens-reveal-close"
                className="px-4 py-2 rounded-md bg-slate-700 hover:bg-slate-600 text-white text-sm"
              >
                {t('printerTokens.revealClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────── Revoke confirmation ─────────── */}
      {revokeTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="printer-tokens-revoke-modal">
          <div className="bg-slate-900 border border-red-500/40 rounded-xl p-6 w-full max-w-md space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2 text-red-300">
              <Ban size={18} /> {t('printerTokens.revokeTitle')}
            </h2>
            <p className="text-sm text-slate-300">
              {t('printerTokens.revokeMsgPre')} <strong className="text-slate-100">{revokeTarget.label}</strong> ({revokeTarget.token_preview}){' '}
              {t('printerTokens.revokeMsgPost')}
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setRevokeTarget(null)}
                disabled={revoking}
                className="px-4 py-2 rounded-md text-slate-300 hover:bg-slate-800 text-sm"
                data-testid="printer-tokens-revoke-cancel"
              >
                {t('printerTokens.cancel')}
              </button>
              <button
                type="button"
                onClick={() => handleRevoke(revokeTarget)}
                disabled={revoking}
                data-testid="printer-tokens-revoke-confirm"
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-500 text-white text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              >
                {revoking && <Loader2 size={14} className="animate-spin" />}
                {t('printerTokens.revoke')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PrinterTokensPage;
